// frontend/src/views/ComparisonView.tsx

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MessageCircle } from "lucide-react";
import DocumentViewer from "../components/DocumentViewer";
import { FallbackPreview } from "../components/DocumentViewer";
import { getDocument, type Document } from "../services/api";
import {
  getComments,
  createComment,
  createReply,
  type Comment,
} from "../services/comments";
import { CommentThread } from "../components/CommentThread";
import LoadingSpinner from "../components/ui/LoadingSpinner";

interface DocumentColors {
  [key: number]: {
    border: string;
    badge: string;
  };
}

const DOCUMENT_COLORS: DocumentColors = {
  1: { border: "border-blue-200", badge: "bg-blue-100 text-blue-800" },
  2: { border: "border-green-200", badge: "bg-green-100 text-green-800" },
  3: { border: "border-purple-200", badge: "bg-purple-100 text-purple-800" },
};

const DocumentPreview: React.FC<{ 
  document: Document; 
  index: number; 
  headerHeight: number; 
}> = ({
  document,
  index,
  headerHeight,
}) => {
  const handleViewerError = useCallback((error: Error) => {
    // Handle viewer error
  }, []);

  const colorMap: Record<number, DocumentColors[keyof DocumentColors]> = {
    0: DOCUMENT_COLORS[1],
    1: DOCUMENT_COLORS[2],
    2: DOCUMENT_COLORS[3],
  };
  const colors = colorMap[index];

  return (
    <div className="bg-white rounded-lg shadow-sm h-full flex flex-col">
      <div 
        className="px-4 pt-3 pb-4 border-b shrink-0 flex flex-col justify-center" 
        style={{ height: `${headerHeight}px` }}
      >
        <div className="space-y-2">
          <div>
            <h2 className="font-medium text-gray-900 leading-tight text-sm md:text-base line-clamp-2">
              {document.title || document.filename}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              {document.title && (
                <p className="text-xs text-gray-500 truncate flex-1 min-w-0">{document.filename}</p>
              )}
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {new Date(document.exam_date).toLocaleDateString()}
              </span>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-1 min-h-0">
            {document.tags?.length > 0 ? (
              document.tags.slice(0, 6).map((tag) => (
                <span
                  key={tag}
                  className={`px-1.5 py-0.5 rounded text-xs ${colors.badge} whitespace-nowrap`}
                >
                  {tag}
                </span>
              ))
            ) : (
              <span className="px-2 py-0.5 text-xs text-gray-400 italic">
                No tags
              </span>
            )}
            {document.tags?.length > 6 && (
              <span className={`px-1.5 py-0.5 rounded text-xs ${colors.badge} opacity-70`}>
                +{document.tags.length - 6}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className={`flex-1 flex flex-col border-2 ${colors.border} min-h-0`}>
        {document.URL || document.file_path ? (
          <DocumentViewer
            document={document}
            showControls={true}
            onError={handleViewerError}
          />
        ) : (
          <FallbackPreview
            type={document.type}
            message="Anteprima non disponibile."
          />
        )}
      </div>
    </div>
  );
};

const ComparisonView: React.FC = () => {
  const navigate = useNavigate();
  const { ids } = useParams<{ ids: string }>();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  const calculateHeaderHeight = useCallback((doc: Document) => {
    const baseHeight = 80; // pt-3 pb-4 + space-y-2 
    const titleLines = Math.min(2, Math.ceil((doc.title || doc.filename).length / 35));
    const tagRows = Math.ceil(Math.min(6, doc.tags?.length || 0) / 3); // max 6 tags, ~3 per row
    const metadataHeight = 20; // filename + date row
    return Math.max(120, baseHeight + (titleLines - 1) * 20 + metadataHeight + tagRows * 24);
  }, []);

  const maxHeaderHeight = useMemo(() => {
    if (documents.length === 0) return 120;
    return Math.max(...documents.map(calculateHeaderHeight));
  }, [documents, calculateHeaderHeight]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const documentIds = ids?.split(",").map(Number) || [];
        const docPromises = documentIds.map((id) => getDocument(id));
        const docResponses = await Promise.all(docPromises);
        setDocuments(docResponses.map((r) => r.data));
        const commentsResponse = await getComments(documentIds);
        setComments(commentsResponse.data);
        setError(null);
      } catch (err: any) {
        setError("Error loading data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [ids]);

  const handleNewComment = async () => {
    if (!newComment.trim() || !selectedDocumentId || isSubmittingComment)
      return;

    try {
      setIsSubmittingComment(true);
      const response = await createComment(parseInt(selectedDocumentId, 10), {
        content: newComment,
      });

      if (response.success) {
        const documentIds = ids?.split(",").map(Number) || [];
        const commentsResponse = await getComments(documentIds);
        setComments(commentsResponse.data);
        setNewComment("");
        setSelectedDocumentId("");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Errore durante l'invio del commento";
      setError(message);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleReplySubmit = async (content: string, parentId: number) => {
    try {
      const response = await createReply(parentId, { content });

      if (response.success) {
        const documentIds = ids?.split(",").map(Number) || [];
        const commentsResponse = await getComments(documentIds);
        if (commentsResponse.success) {
          setComments(commentsResponse.data);
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Errore durante l'invio della risposta";
      setError(message);
    }
  };

  if (loading) {
    return (
      <div className="w-full max-w-7xl mx-auto p-6 flex flex-col flex-1">
        <LoadingSpinner message="Caricamento Documenti..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-7xl mx-auto p-6 flex flex-col flex-1">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">{error}</h2>
          <button
            onClick={() => navigate("/documents")}
            className="mt-4 text-blue-600 hover:underline"
          >
            Torna alla lista documenti
          </button>
        </div>
      </div>
    );
  }

  if (!documents || documents.length < 2) {
    return (
      <div className="w-full max-w-7xl mx-auto p-6 flex flex-col flex-1">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">
            Seleziona almeno due documenti da comparare
          </h2>
          <button
            onClick={() => navigate("/documents")}
            className="mt-4 text-blue-600 hover:underline"
          >
            Torna alla lista documenti
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto p-4 md:p-6 flex flex-col flex-1">
      <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-6">
        <button
          onClick={() => navigate("/documents")}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-xl md:text-2xl font-bold">Comparazione Documenti</h1>
      </div>

      <div className="mb-6 md:mb-8">
        <div
          className={`grid gap-4 md:gap-6 auto-rows-fr ${
            documents.length === 2
              ? "grid-cols-1 lg:grid-cols-2"
              : documents.length === 3
              ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
              : "grid-cols-1"
          }`}
        >
          {documents.map((doc, index) => (
            <DocumentPreview 
              key={doc.id} 
              document={doc} 
              index={index} 
              headerHeight={maxHeaderHeight}
            />
          ))}
        </div>
      </div>

      <div className="flex-1">
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle className="text-gray-500" />
          <h2 className="text-lg font-semibold">Commenti</h2>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
            <button 
              onClick={() => setError(null)} 
              className="ml-2 text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Aggiungi un commento..."
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
          />
          <div className="flex items-center justify-end gap-3 mt-2">
            <select
              value={selectedDocumentId}
              onChange={(e) => setSelectedDocumentId(e.target.value)}
              className="px-3 py-2 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Seleziona documento</option>
              {documents.map((doc) => (
                <option key={doc.id} value={doc.id}>
                  {doc.filename}
                </option>
              ))}
            </select>
            <button
              onClick={handleNewComment}
              disabled={
                !newComment.trim() || !selectedDocumentId || isSubmittingComment
              }
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmittingComment ? "Invio in corso..." : "Invia commento"}
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {comments.map((comment) => {
            // Find the document index in the current documents array
            const documentIndex = documents.findIndex(doc => doc.id === comment.document_id);
            // Use same color mapping as DocumentPreview
            const colorMap: Record<number, DocumentColors[keyof DocumentColors]> = {
              0: DOCUMENT_COLORS[1],
              1: DOCUMENT_COLORS[2],
              2: DOCUMENT_COLORS[3],
            };
            const colors = colorMap[documentIndex] || DOCUMENT_COLORS[1]; // fallback to blue

            return (
              <CommentThread
                key={comment.id}
                comment={comment}
                documents={documents}
                onReplySubmit={handleReplySubmit}
                colorClass={colors.border}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ComparisonView;
